import { Injectable } from "@angular/core";
import { MatSort, SortDirection } from "@angular/material/sort";
import { GS1LoadingService } from "@gs1france/gs1-components-library";
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, of, Subject } from "rxjs";
import { C1UserInfo, Privilege } from "src/app/models/c1UserInfo.model";
import { SearchCriteria, SearchGLNRequest, SearchGLNResults } from "src/app/models/search.model";
import { Contact, ContactInfo, Gcps } from "../../../models/contact.model";
import { GLNInterface } from "../../../models/gln.model";
import { ContactService } from "../contact/contact.service";
import { GLNService } from "../gln/gln.service";


/**
 * A service that handles all of the storage and retrieval of data.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService {

  private glns!: GLNInterface[];
  private glnsSub: BehaviorSubject<GLNInterface[]> = new BehaviorSubject<GLNInterface[]>([]);
  private glnLoading = false;
  public forceGLNRefresh = false;

  private accountPage: {page: number, sortBy: string, sortDirection: SortDirection, search: string} = {
    page: 0,
    sortBy: 'isMain',
    sortDirection: 'desc',
    search: ''
  };

  private contact!: Contact;
  private contactSub: Subject<Contact> = new Subject<Contact>();
  private contactLoading = false;

  private contactInfo!: ContactInfo;
  private contactInfoSub: Subject<ContactInfo> = new Subject<ContactInfo>();
  private contactInfoLoading = false;

  private loaded = false;

  private c1UserInfo!: C1UserInfo;
  private c1UserInfoSub: Subject<C1UserInfo> = new Subject<C1UserInfo>();

  currentSearch?: SearchGLNRequest;
  currentSearchResults?: SearchGLNResults;

  constructor(private glnService: GLNService, private contactService: ContactService, private _loading: GS1LoadingService) {}

  /**
   * Sets up the app.
   * @returns None
   */
  setUpApp(): Observable<ContactInfo | false> {
    if (this.loaded) {
      return of(this.contactInfo)
    }
    this._loading.startLoading('setup');
    return combineLatest([this.getContactInfo()]).pipe(
      map(([contact]) => {
        this.loaded = true;
        this._loading.stopLoading('setup');
        if (contact)
          return contact;
        return false
      })
    )
  }

  /**
   * Gets the GLNs for the current site.
   * @param {boolean} [forceRefresh=false] - Whether to force a refresh of the GLNs.
   * @returns {Promise<string[]>} A promise that resolves to an array of GLNs.
   */
  async getGLNs() {
    if (!this.glns && !this.glnLoading || this.forceGLNRefresh === true) {
      this.glnLoading = true;
      this.forceGLNRefresh = false;
      await this.loadGLNs();
      this.glnLoading = false;
    } else if (this.glnLoading) {
      return firstValueFrom(this.glnsSub)
    }
    return [...this.glns];
  }

  /**
   * Loads the GLNs from the database.
   * @returns None
   */
  private async loadGLNs() {
    try {
      this.glns = await this.glnService.getAccountGLNs();
      this.glnsSub.next(this.glns);
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Gets the contact information for the site.
   * @returns {Promise<Contact>}
   */
  async getContactInfo() {
    if (!this.contactLoading && !this.contactInfo) {
      this.contactLoading = true;
      await this.loadContactInfo();
      this.contactLoading = false;
    } else if (this.contactLoading) {
      return firstValueFrom(this.contactInfoSub)
    }
    return {...this.contactInfo};
  }

  async getContact() {
    if (this.contact) {
      return {...this.contact}
    } else {
      return firstValueFrom(this.contactSub);
    }
  }

  /**
   * Updates the contact information for the user.
   * @param {Contact} contact - the new contact information for the user.
   * @returns None
   */
  updateContact(contact: Contact) {
    this.contact = contact;
    this.contactSub.next(this.contact);
  }

  updateContactInfo(contact: ContactInfo) {
    this.contactInfo = contact;
    this.contactInfoSub.next(this.contactInfo);
  }

  /**
   * Loads the contact information from the database.
   * @returns None
   */
  private async loadContactInfo() {
    try {
      this.contactInfo = await this.contactService.getInfoContact();
      this.contactInfoSub.next(this.contactInfo);
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Update the C1 user info object.
   * @param {C1UserInfo} user - the C1 user info object
   * @returns None
   */
  handleConnect1Infos(user: C1UserInfo) {
    this.c1UserInfo = user;
    const parsedClaims = Array.isArray(user.contact_info) ? JSON.parse(user.contact_info[0]) : JSON.parse(user.contact_info);
    this.c1UserInfoSub.next(this.c1UserInfo);
    const contact : Contact = {
      email: user.email,
      companyName: user.company_name,
      contactId: user.contact_id,
      idCompany: user.company_id,
      gln: user.company_gln,
      id: parsedClaims.Id,
      professionalPhone: parsedClaims.ProfessionalPhone,
      mobilePhone: parsedClaims.MobilePhone,
      addresses: parsedClaims.Addresses,
      jobId: parsedClaims.JobId,
      gender: parsedClaims.Gender,
      firstName: parsedClaims.FirstName || user.email,
      lastName: parsedClaims.LastName
    }
    this.updateContact(contact);
  }

  /**
   * Returns true if the user is an admin.
   * @returns {boolean} - true if the user is an admin.
   */
  async isAdmin() {
    if (this.c1UserInfo) {
      return this.c1UserInfo.privileges.includes('Administrateur')
    } else {
      return firstValueFrom(this.c1UserInfoSub.pipe(map(userInfo => userInfo.privileges.includes('Administrateur'))))
    }
  }

  /**
   * Sets the current search and search results.
   * @param {SearchGLNRequest | undefined} search - the search object.
   * @param {SearchGLNResults | undefined} results - the search results object.
   * @returns None
   */
  setCurrentSearch(search: SearchGLNRequest | undefined, results?: SearchGLNResults) {
    this.currentSearch = search;
    this.currentSearchResults = results;
  }

  /**
   * Get the current search string.
   * @returns {SearchGLNRequest | null} The current search data.
   */
  getCurrentSearch() {
    return this.currentSearch;
  }

  /**
   * Returns the current search results.
   * @returns {SearchResult[]} - the current search results.
   */
  getCurrentSearchResults() {
    return this.currentSearchResults;
  }

  saveAccountPage(page: number, sortBy: string, sortDirection: SortDirection, search: string) {
    this.accountPage = {page, sortBy, sortDirection, search};
  }

  getAccountPage() : {page: number, sortBy: string, sortDirection: SortDirection, search: string} {
    return this.accountPage;
  }

  resetAccountPage() {
    this.accountPage = {
      page: 0,
      sortBy: 'isMain',
      sortDirection: 'desc',
      search: ''
    }
  }
}
